<!-- The dialog is a row, not a column: everything the report owns on the left, and the what-if
     panel docked beside ALL of it on the right. Docked inside the content area it was hemmed in by
     the header and the footer, which is what made a battery table need a scrollbar. -->
<div class="modal-shell">

<div class="modal-main">

<div mat-dialog-title class="modal-header">
  <!-- Back. On the native chart it steps back out of a drill one level at a time, on every report,
       and is greyed out until there is a level to go back to. On the SSRS report it does what it
       always did: return from a drillthrough on the remainder report. -->
  <button mat-mini-fab *ngIf="useNativeChart && !isNativePie" class="native-back" (click)="chartCmp?.drillUp()"
    [disabled]="!chartCmp?.drillStack?.length" matTooltip="Back one level">
    <mat-icon>keyboard_backspace</mat-icon>
  </button>
  <button mat-mini-fab *ngIf="!useNativeChart && (isResourceRemainderConsumption$ | async)" (click)="returnLastUrl()" 
  [disabled]="!(isUrlsReturnLength | async)  ? 'disabled' : null">
    <mat-icon>keyboard_backspace</mat-icon>
  </button>
  <svg-icon [src]="getImageUrl(data.category.fpicon?.iconPath!)" [svgStyle]="{'width.px':25,'height.px':25 }">
  </svg-icon>
  <span class="fw-bold">{{data.category.categoryName}}: </span>
  <span>
    {{selectedGroup ? selectedGroup.name : ""}}
  </span>
  <button mat-button class="ms-2" *ngIf="data.category.groups?.length! > 1" (click)="isShowGroups=!isShowGroups">
    Change
  </button>

  <div class="header-checkbox">
    <!-- Sits with the other chart-wide controls rather than in a strip of its own above the tiles.
         Only appears once the chart reports there is something to scale. -->
    <button *ngIf="whatIfAvailable" mat-stroked-button class="me-3 whatif-header-btn"
      [class.whatif-header-btn-on]="showWhatIf"
      [matTooltip]="(showWhatIf ? 'Hide' : 'Show') + ' the what-if panel'"
      (click)="showWhatIf = !showWhatIf">
      What if...
    </button>
    <!-- Written out and stacked, as Compare and Classic chart are in the footer (Jayden, 02/10/26).
         The box says "Hide labels", so ticked means hidden: it shows the opposite of isShowLabel,
         which is still what the chart and the SSRS report's aShowLabel read. -->
    <div class="header-toggles">
      <mat-checkbox [ngModel]="!isShowLabel" (ngModelChange)="isShowLabel = !$event; changeShowLabel()"
        [matTooltip]="(isShowLabel ? 'Hide' : 'Show') + ' the numbers on the chart'">Hide labels
      </mat-checkbox>
      <mat-checkbox [(ngModel)]="isToolbarActive"
        [matTooltip]="(isToolbarActive ? 'Hide' : 'Show') + ' the toolbar for export & print'" (change)="changeToolbar()">Toolbar
      </mat-checkbox>
    </div>
  </div>
</div>

<div mat-dialog-content>
  <div class="modal-body">
    <div *ngIf="isShowGroups && data.category.groups?.length! > 1"
      class="left-body btn-group-vertical scrollbar-primary">
      <h6>Resources</h6>
      <div *ngFor="let group of data.category.groups" class="pt-1 w-100">
        <button class="btn w-100" (click)="changeGroup(group)"
          [ngClass]="{'btn-primary': selectedGroup == group, 'btn-outline-primary': selectedGroup != group}">
          {{group.name}}
        </button>
      </div>
    </div>

    <div class="right-body scrollbar-primary" [class.native-body]="useNativeChart">
      <!-- Error message -->
      <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>

      <!-- Carbon KPI Card -->
      <div *ngIf="isCarbonReport && carbonKpiData" class="carbon-kpi-card shadow-sm">
        <div class="kpi-header d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <h6 class="m-0 fw-bold">Estimated Tree Equivalent</h6>
            <mat-icon class="ms-1 info-icon" style="font-size: 16px; width: 16px; height: 16px; cursor: help;"
              matTooltip="Estimated using 22 kg CO₂ absorbed per mature tree per year. Actual absorption varies by species, age, location, and tree maturity.">
              info
            </mat-icon>
          </div>
          <button mat-icon-button (click)="closeCarbonKpi()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="kpi-body d-flex align-items-center mt-2">
          <div class="kpi-icon-wrapper me-3">
            <mat-icon class="tree-icon" style="font-size: 48px; width: 48px; height: 48px;">forest</mat-icon>
          </div>
          <div class="kpi-content">
            <div class="kpi-main-value">{{ carbonKpiData.treeEquivalent }}</div>
            <div class="kpi-unit-text text-success fw-bold">mature trees/year</div>
          </div>
        </div>
        <div class="kpi-footer mt-3 pt-2 border-top">
          <div class="kpi-secondary text-muted">
            <small>{{ carbonKpiData.value }} {{ carbonKpiData.unit }} CO₂ emissions</small>
          </div>
          <div class="kpi-formula text-muted">
            <small>Formula: {{ carbonKpiData.formula }}</small>
          </div>
        </div>
      </div>

      <!-- Loading spinner, for the SSRS report. The native chart says "Loading..." over its own box,
           so the dialog's spinner flashing on and off above it was only noise. -->
      <div *ngIf="!errorMessage && !useNativeChart" class="spinner" [hidden]="!((isLoading$ | async) || isUrlSafeLoad)">
        <mat-spinner class="spinner-body"></mat-spinner>
      </div>

      <!-- Why this chart opened on an older window than another. See openOnLatestReading. -->
      <div *ngIf="useNativeChart && latestReadingNote && !errorMessage" class="native-latest-note">
        <mat-icon>history</mat-icon>{{ latestReadingNote }}
      </div>

      <!-- Dates that cannot make a window. The chart keeps the last one it had. See commitNativeDates. -->
      <div *ngIf="useNativeChart && dateMessage" class="native-latest-note native-date-message" role="alert">
        <mat-icon>error_outline</mat-icon>{{ dateMessage }}
      </div>

      <!-- Native chart, feature-flagged. Falls back to the SSRS iframe for every other report. -->
      <!-- On the page from the start, and never taken off it for the latest-reading lookup: ready
           holds it until the window is known, so it neither collapses nor loads twice. -->
      <app-resource-series-chart *ngIf="useNativeChart && !isNativePie && !errorMessage"
              [ready]="nativeWindowReady"
              [siteId]="nativeSiteId"
              [readingTypeId]="nativeReadingTypeId"
              [period]="nativePeriod"
              [dateLeft]="nativeDateLeft"
              [dateRight]="nativeDateRight"
              [reportUrl]="nativeReportUrl"
              [groupType]="nativeGroupType"
              [groupId]="nativeGroupId"
              [compare]="isCompare"
              [compareLeft]="nativeCompareLeft"
              [compareRight]="nativeCompareRight"
              [showLabels]="isShowLabel"
              [showToolbar]="isToolbarActive"
              [chartTitle]="nativeChartTitle"
              [(showWhatIf)]="showWhatIf"
              (whatIfAvailable)="whatIfAvailable = $event"
              (stateChanged)="onChartStateChanged()">
      </app-resource-series-chart>

      <!-- Same window, split into shares instead of bucketed over time. 266 of the 301 categories
           ask for a pie in their ShowFormat, so this is not a side view. -->
      <app-resource-series-pie *ngIf="isNativePie && !errorMessage && nativeWindowReady"
              [siteId]="nativeSiteId"
              [readingTypeId]="nativeReadingTypeId"
              [period]="nativePeriod"
              [dateLeft]="nativeDateLeft"
              [dateRight]="nativeDateRight"
              [reportUrl]="nativeReportUrl"
              [groupType]="nativeGroupType"
              [groupId]="nativeGroupId">
      </app-resource-series-pie>

      <!-- Report iframe -->
      <iframe *ngIf="!useNativeChart && !isNativePie && !isUrlSafeLoad && !errorMessage && current_url"
              width="100%" height="550px" frameBorder="0"
              [src]="current_url"></iframe>
    </div>
  </div>

</div>

<div mat-dialog-actions class="d-block">
  <div class="modal-footer">
    <div *ngIf="isShowDatepicker" class="date-and-period">
      <mat-button-toggle-group [(ngModel)]="selectedPeriod" (change)="changePeriod($event.value)">
        <mat-button-toggle *ngFor="let period of aPeriodList" [value]="period"
          [matTooltip]="'Click to select ' + period +  ' timescale'">{{period}}</mat-button-toggle>
      </mat-button-toggle-group>

      <mat-form-field style="margin-bottom: -1.25em">
        <mat-label>Start date</mat-label>
        <input matInput [(ngModel)]="startDate" [matDatepicker]="startDatePicker" (dateChange)="onDateChosen('start')">
        <mat-datepicker-toggle matSuffix [for]="startDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #startDatePicker></mat-datepicker>
      </mat-form-field>

      <mat-form-field style="margin-bottom: -1.25em">
        <mat-label>End date</mat-label>
        <input matInput [(ngModel)]="endDate" [matDatepicker]="endDatePicker" (dateChange)="onDateChosen('end')">
        <mat-datepicker-toggle matSuffix [for]="endDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #endDatePicker></mat-datepicker>
      </mat-form-field>
    </div>

    <!-- Drives both paths now. On the SSRS iframe changeChartType sets aChartType on the report
         URL; on the native path it swaps the bar component for the pie one. It was hidden on the
         native path in a1cbd29 because there was no native pie to switch to and the control sat
         there doing nothing when clicked, reported live from the 11/09/26 demo. There is one now. -->
    <!-- Hidden under a native Compare, which already shows the bars and a pie for each period. -->
    <mat-button-toggle-group *ngIf="!(useNativeChart && isCompare)" [(ngModel)]="chartType" (change)="changeChartType($event.value)">
      <mat-button-toggle *ngFor="let chart of chartTypeList" [value]="chart" [matTooltip]="chart.tooltip">
        <mat-icon>{{chart.iconName}}</mat-icon>
      </mat-button-toggle>
    </mat-button-toggle-group>

    <div class="ml-4">
      <div *ngIf="isShowCompare">
        <mat-checkbox [(ngModel)]="isCompare" (change)="changeCompare()">Compare</mat-checkbox>
      </div>
      <!-- The native chart is the default. This falls back to the old SSRS report. -->
      <div *ngIf="canUseNativeChart">
        <mat-checkbox [(ngModel)]="useClassicChart">Classic chart</mat-checkbox>
      </div>
    </div>

  </div>
</div>

<mat-dialog-actions align="end">
  <button mat-button (click)="onNoClick()">
    Close
  </button>
</mat-dialog-actions>

</div>

<!-- The chart declares this panel; the dialog decides where it goes. Rendering it here rather than
     inside the chart is the whole point - it gets the modal's height, not the chart's. -->
<aside class="whatif-dock" *ngIf="showWhatIf && chartCmp?.whatIfTpl">
  <ng-container *ngTemplateOutlet="chartCmp!.whatIfTpl"></ng-container>
</aside>

</div>
